<template>
  <div class="landing-wrapper">
    <v-container class="py-8 py-md-12" fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-alert v-if="error" type="error" class="mb-4" closable @click:close="error = ''">
            {{ error }}
          </v-alert>

          <div v-if="loading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary" size="48" />
          </div>

          <div v-else class="obra-form-panel">
            <div class="obra-form-header">
              <v-icon size="22" color="#ffffff">mdi-account-plus-outline</v-icon>
              <span>{{ editing ? 'EDITAR CLIENTE' : 'NUEVO CLIENTE' }}</span>
            </div>

            <div class="obra-form-body">
              <h3 class="cf-section">DATOS DE LA EMPRESA</h3>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.company_name"
                    label="Nombre"
                    variant="outlined"
                    :error-messages="fieldError('company_name')"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.client_type"
                    :items="clientTypeOptions"
                    item-title="label"
                    item-value="value"
                    label="Rubro"
                    variant="outlined"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-combobox
                    v-model="form.company_type"
                    :items="companyTypeOptions"
                    label="Tipo de empresa"
                    hint="Elige uno existente o escribe uno nuevo"
                    persistent-hint
                    variant="outlined"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="form.social_name" label="Razón social" variant="outlined" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.rut"
                    label="RUT de la empresa"
                    placeholder="12345678-9"
                    variant="outlined"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="form.region" label="Región" variant="outlined" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="form.city" label="Comuna" variant="outlined" />
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="form.address" label="Dirección comercial" variant="outlined" />
                </v-col>
              </v-row>

              <h3 class="cf-section cf-section-spaced">CONTACTO PRINCIPAL</h3>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.first_name"
                    label="Nombres"
                    variant="outlined"
                    :error-messages="fieldError('first_name')"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="form.last_name" label="Apellidos" variant="outlined" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.email"
                    label="Correo"
                    type="email"
                    variant="outlined"
                    :error-messages="fieldError('email')"
                    hint="Es también el usuario con el que inicia sesión. Sin correo, el cliente no puede entrar ni recibe campañas."
                    persistent-hint
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="form.phone" label="Teléfono" variant="outlined" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.position"
                    label="Cargo"
                    variant="outlined"
                    :error-messages="fieldError('position')"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    v-model="form.is_active"
                    label="Cuenta activa"
                    color="primary"
                    hide-details
                  />
                </v-col>
              </v-row>

              <div
                v-for="(contact, index) in form.other_contacts"
                :key="contact.key"
                class="cf-contact"
              >
                <div class="cf-contact-head">
                  <h3 class="cf-section">CONTACTO {{ index + 2 }}</h3>
                  <button
                    v-if="!contact.id"
                    type="button"
                    class="cf-ghost-btn"
                    @click="removeContact(index)"
                  >
                    QUITAR
                  </button>
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="contact.first_name"
                      label="Nombres"
                      variant="outlined"
                      :error-messages="contactError(index, 'first_name')"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="contact.last_name" label="Apellidos" variant="outlined" />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="contact.email"
                      label="Correo"
                      type="email"
                      variant="outlined"
                      :error-messages="contactError(index, 'email')"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="contact.phone" label="Teléfono" variant="outlined" />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="contact.position" label="Cargo" variant="outlined" />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-switch
                      v-model="contact.is_active"
                      label="Cuenta activa"
                      color="primary"
                      hide-details
                    />
                  </v-col>
                </v-row>
              </div>

              <button type="button" class="cf-add-btn" @click="addContact">
                <v-icon size="18">mdi-plus</v-icon>
                AGREGAR OTRO CONTACTO
              </button>

              <p class="cf-note">
                La cuenta se crea sin contraseña: el cliente la define desde
                "¿Olvidaste tu contraseña?" con su correo.
              </p>

              <div class="obra-form-actions">
                <button
                  v-if="editing"
                  type="button"
                  class="cf-ghost-btn cf-danger-btn"
                  :disabled="saving || deleting"
                  @click="remove"
                >
                  {{ deleting ? 'ELIMINANDO…' : 'ELIMINAR CLIENTE' }}
                </button>
                <button type="button" class="cf-ghost-btn" @click="goBack">
                  CANCELAR Y VOLVER
                </button>
                <button
                  type="button"
                  class="cf-primary-btn"
                  :disabled="saving"
                  @click="save"
                >
                  {{ saving ? 'GUARDANDO…' : 'GUARDAR' }}
                </button>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <teatro-footer />
  </div>
</template>

<script>
import {
  fetchClient,
  createClient,
  updateClient,
  deleteClient,
  fetchClientFilterOptions,
} from "@/services/teatroApi";


const EMPTY = {
  company_name: "",
  client_type: "",
  company_type: "",
  social_name: "",
  rut: "",
  address: "",
  city: "",
  region: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  position: "",
  is_active: true,
  other_contacts: [],
};

// Clave local para el v-for: los contactos nuevos todavía no tienen id.
let contactKey = 0;

function newContact(data = {}) {
  return {
    key: ++contactKey,
    id: data.id || null,
    first_name: data.first_name || "",
    last_name: data.last_name || "",
    email: data.email || "",
    phone: data.phone || "",
    position: data.position || "",
    is_active: data.is_active !== false,
  };
}

export default {
  name: "ClientForm",
  props: {
    clientId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      form: { ...EMPTY, other_contacts: [] },
      deleting: false,
      clientTypeOptions: [],
      companyTypeOptions: [],
      fieldErrors: {},
      loading: true,
      saving: false,
      error: "",
    };
  },
  computed: {
    editing() {
      return this.clientId !== null && this.clientId !== "";
    },
  },
  async created() {
    await this.loadAuth();
    await this.load();
  },
  methods: {
    async loadAuth() {
      try {
        await this.$store.dispatch("fetchIsAuthenticated");
      } catch (e) {
        console.error(e);
      }
    },
    async load() {
      this.loading = true;
      try {
        const options = await fetchClientFilterOptions().catch((e) => {
          console.error("No se pudieron cargar los rubros", e);
          return {};
        });
        this.clientTypeOptions = options.client_types || [];
        this.companyTypeOptions = options.company_types || [];
        if (this.editing) {
          const c = await fetchClient(this.clientId);
          this.form = {
            company_name: c.company_name || "",
            client_type: c.client_type || "",
            company_type: c.company_type || "",
            social_name: c.social_name || "",
            rut: c.rut || "",
            address: c.address || "",
            city: c.city || "",
            region: c.region || "",
            first_name: c.first_name || "",
            last_name: c.last_name || "",
            email: c.email || "",
            phone: c.phone || "",
            position: c.position || "",
            is_active: c.is_active !== false,
            other_contacts: (c.other_contacts || []).map(newContact),
          };
        }
      } catch (e) {
        this.error = "No se pudo cargar el cliente.";
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    fieldError(name) {
      return this.fieldErrors[name] || [];
    },
    contactError(index, name) {
      // Llegan como lista (validación de cada contacto) o como objeto por
      // índice (correos repetidos); el mensaje general sí es una lista de textos.
      const errors = this.fieldErrors.other_contacts;
      const contact = errors && errors[index];
      return (contact && typeof contact === "object" && contact[name]) || [];
    },
    addContact() {
      this.form.other_contacts.push(newContact());
    },
    removeContact(index) {
      this.form.other_contacts.splice(index, 1);
    },
    async save() {
      this.saving = true;
      this.error = "";
      this.fieldErrors = {};
      try {
        // Los selectores con "clearable" dejan null al limpiarse.
        const payload = {
          ...this.form,
          client_type: this.form.client_type || "",
          company_type: (this.form.company_type || "").trim(),
          // eslint-disable-next-line no-unused-vars
          other_contacts: this.form.other_contacts.map(({ key, ...contact }) => contact),
        };
        if (this.editing) {
          await updateClient(this.clientId, payload);
        } else {
          await createClient(payload);
        }
        window.location.href = "/clientes/";
      } catch (e) {
        const data = e.response?.data;
        if (data && typeof data === "object" && !Array.isArray(data)) {
          // DRF devuelve { campo: ["mensaje"] }; lo demás va al alerta general.
          this.fieldErrors = data;
          const contactsError = Array.isArray(data.other_contacts)
            && typeof data.other_contacts[0] === "string"
            ? data.other_contacts[0]
            : "";
          this.error = data.detail || contactsError || "Revisa los campos marcados.";
        } else {
          this.error = "No se pudo guardar el cliente.";
        }
        console.error(e);
        this.saving = false;
      }
    },
    // Borrado definitivo de la cuenta (y de la empresa si queda sin
    // contactos); las reservas que hizo se conservan.
    async remove() {
      const who = [this.form.company_name, this.form.email].filter(Boolean).join(" · ");
      const ok = window.confirm(
        `¿Eliminar al cliente ${who}?\n\nSe borra su cuenta de forma definitiva. ` +
          "Sus reservas se conservan con el nombre y correo registrados."
      );
      if (!ok) return;
      this.deleting = true;
      this.error = "";
      try {
        await deleteClient(this.clientId);
        window.location.href = "/clientes/";
      } catch (e) {
        const data = e.response?.data;
        this.error = (data && data.detail) || "No se pudo eliminar el cliente.";
        console.error(e);
        this.deleting = false;
      }
    },
    goBack() {
      window.location.href = "/clientes/";
    },
  },
};
</script>

<style scoped>
/* Mismo panel de formulario que PlayForm / CompanyProfile / ProductoraForm. */
.obra-form-panel {
  max-width: 1080px;
  margin: 0 auto;
  background: rgba(8, 5, 5, 0.66);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}
.obra-form-header {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 66px;
  padding: 0 32px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 1.4px;
}
.obra-form-body {
  padding: 30px 34px 28px;
}
.obra-form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 26px;
  margin-top: 18px;
}
.obra-form-panel :deep(.v-field) {
  background: #ffffff !important;
  border-radius: 8px !important;
}
.obra-form-panel :deep(.v-field__outline) {
  --v-field-border-opacity: 0;
  color: transparent !important;
}
.obra-form-panel :deep(.v-field__input),
.obra-form-panel :deep(.v-field input) {
  color: #1a1a1a !important;
}
.obra-form-panel :deep(.v-field-label) {
  color: #7a7a7a !important;
  opacity: 1;
}
.obra-form-panel :deep(.v-field-label.v-field-label--floating) {
  color: #ffffff !important;
  background: #17100f;
  padding: 1px 6px;
  border-radius: 4px;
}
.obra-form-panel :deep(.v-messages),
.obra-form-panel :deep(.v-messages__message) {
  color: rgba(255, 255, 255, 0.75) !important;
  opacity: 1;
}
.obra-form-panel :deep(.v-switch .v-label) {
  color: var(--color-theater-text) !important;
  opacity: 1;
}

.cf-section {
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 1.2px;
  margin: 0 0 14px;
}

.cf-section-spaced {
  margin-top: 30px;
}

.cf-contact {
  margin-top: 26px;
  padding-top: 22px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.cf-contact-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.cf-contact-head .cf-section {
  margin: 0;
}

.cf-contact-head + .v-row {
  margin-top: 6px;
}

.cf-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 22px;
  background: transparent;
  border: 1px dashed rgba(255, 255, 255, 0.35);
  border-radius: 8px;
  padding: 10px 18px;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.cf-add-btn:hover {
  border-color: #c8102e;
  background: rgba(200, 16, 46, 0.12);
}

.cf-note {
  margin-top: 18px;
  color: var(--color-theater-text-muted);
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
}

.cf-ghost-btn {
  background: transparent;
  border: none;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  cursor: pointer;
  padding: 12px 8px;
  transition: opacity 0.2s ease;
}

.cf-danger-btn {
  margin-right: auto;
  color: #ff8a95;
  border-color: rgba(200, 16, 46, 0.6);
}

.cf-ghost-btn:hover {
  opacity: 0.72;
}

.cf-primary-btn {
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 30px;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.cf-primary-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(200, 16, 46, 0.4);
}

.cf-primary-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
</style>
